/**
 * Secure Upload Link Service
 * TASK-361: Document Management & Compliance System
 *
 * Service for managing secure upload links that allow clients to upload
 * documents through a public portal.
 */

import { httpClient } from '../lib/httpClient';
import type {
  SecureUploadLink,
  CreateSecureUploadLinkRequest,
  SecureUploadLinkCreatedResponse,
  SecureUploadLinkListResponse,
  UploadLinkStats,
  QuarantineDocument,
  QuarantineListResponse,
  AcceptDocumentRequest,
  AcceptDocumentResponse,
  RejectDocumentRequest,
  RejectDocumentResponse,
  ComplianceSummary,
  ExpiringDocument,
  ExpiringDocumentsListResponse,
  ComplianceByCategory,
} from '../types/documentCompliance';

class SecureUploadLinkService {
  private readonly basePath = '/api/secure-links';
  private readonly quarantinePath = '/api/quarantine';
  private readonly compliancePath = '/api/documents/compliance';

  // ============================================================================
  // Secure Upload Link Methods
  // ============================================================================

  /**
   * Create a new secure upload link for a client.
   */
  async createLink(request: CreateSecureUploadLinkRequest): Promise<SecureUploadLinkCreatedResponse> {
    return httpClient.post<SecureUploadLinkCreatedResponse>(this.basePath, request);
  }

  /**
   * List secure upload links with optional filtering.
   */
  async listLinks(params?: {
    client_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<SecureUploadLinkListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.client_id) queryParams.append('client_id', params.client_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const url = query ? `${this.basePath}?${query}` : this.basePath;

    return httpClient.get<SecureUploadLinkListResponse>(url);
  }

  /**
   * Get details of a specific upload link.
   */
  async getLink(linkId: string): Promise<SecureUploadLink> {
    return httpClient.get<SecureUploadLink>(`${this.basePath}/${linkId}`);
  }

  /**
   * Revoke an active upload link.
   */
  async revokeLink(linkId: string): Promise<{ success: boolean; message: string }> {
    return httpClient.delete(`${this.basePath}/${linkId}`);
  }

  /**
   * Resend the upload link email to the client.
   */
  async resendEmail(linkId: string, customMessage?: string): Promise<{ success: boolean; message: string }> {
    return httpClient.post(`${this.basePath}/${linkId}/send-email`, {
      custom_message: customMessage,
    });
  }

  /**
   * Get all upload links for a specific client.
   */
  async getClientLinks(clientId: string): Promise<{ client_id: string; total: number; links: SecureUploadLink[] }> {
    return httpClient.get(`${this.basePath}/client/${clientId}`);
  }

  /**
   * Get upload link statistics for the account.
   */
  async getStats(): Promise<UploadLinkStats> {
    return httpClient.get<UploadLinkStats>(`${this.basePath}/stats`);
  }

  /**
   * Extend the upload limit for a link.
   */
  async extendLimit(linkId: string, additionalUploads: number = 1): Promise<{ success: boolean; message: string }> {
    return httpClient.post(`${this.basePath}/${linkId}/extend`, {
      additional_uploads: additionalUploads,
    });
  }

  // ============================================================================
  // Quarantine Methods
  // ============================================================================

  /**
   * List quarantine documents awaiting review.
   */
  async listQuarantineDocuments(params?: {
    client_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<QuarantineListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.client_id) queryParams.append('client_id', params.client_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const url = query ? `${this.quarantinePath}?${query}` : this.quarantinePath;

    return httpClient.get<QuarantineListResponse>(url);
  }

  /**
   * Get count of pending quarantine documents.
   */
  async getPendingCount(): Promise<{ pending_count: number }> {
    return httpClient.get(`${this.quarantinePath}/pending-count`);
  }

  /**
   * Get a specific quarantine document.
   */
  async getQuarantineDocument(docId: string): Promise<QuarantineDocument> {
    return httpClient.get<QuarantineDocument>(`${this.quarantinePath}/${docId}`);
  }

  /**
   * Get presigned URL for viewing a quarantine document.
   */
  async getQuarantinePresignedUrl(docId: string, expires: number = 3600): Promise<{
    url: string;
    expires_in: number;
    filename: string;
    mime_type: string;
  }> {
    return httpClient.get(`${this.quarantinePath}/${docId}/presigned-url?expires=${expires}`);
  }

  /**
   * Accept a quarantine document and move to client documents.
   */
  async acceptDocument(docId: string, request: AcceptDocumentRequest): Promise<AcceptDocumentResponse> {
    return httpClient.post<AcceptDocumentResponse>(`${this.quarantinePath}/${docId}/accept`, request);
  }

  /**
   * Reject a quarantine document.
   */
  async rejectDocument(docId: string, request: RejectDocumentRequest): Promise<RejectDocumentResponse> {
    return httpClient.post<RejectDocumentResponse>(`${this.quarantinePath}/${docId}/reject`, request);
  }

  /**
   * Get all quarantine documents for a specific upload link.
   */
  async getLinkDocuments(linkId: string): Promise<{
    link_id: string;
    total: number;
    documents: QuarantineDocument[];
  }> {
    return httpClient.get(`${this.quarantinePath}/link/${linkId}`);
  }

  // ============================================================================
  // Compliance Dashboard Methods
  // ============================================================================

  /**
   * Get overall compliance summary.
   */
  async getComplianceSummary(warningDays: number = 30): Promise<ComplianceSummary> {
    return httpClient.get<ComplianceSummary>(`${this.compliancePath}/summary?warning_days=${warningDays}`);
  }

  /**
   * Get documents expiring within specified days.
   */
  async getExpiringDocuments(params?: {
    days_ahead?: number;
    doc_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<ExpiringDocumentsListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.days_ahead) queryParams.append('days_ahead', params.days_ahead.toString());
    if (params?.doc_type) queryParams.append('doc_type', params.doc_type);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const url = query ? `${this.compliancePath}/expiring?${query}` : `${this.compliancePath}/expiring`;

    return httpClient.get<ExpiringDocumentsListResponse>(url);
  }

  /**
   * Get expired documents.
   */
  async getExpiredDocuments(params?: {
    doc_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ total: number; documents: ExpiringDocument[] }> {
    const queryParams = new URLSearchParams();
    if (params?.doc_type) queryParams.append('doc_type', params.doc_type);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const query = queryParams.toString();
    const url = query ? `${this.compliancePath}/expired?${query}` : `${this.compliancePath}/expired`;

    return httpClient.get(url);
  }

  /**
   * Get compliance breakdown by document category.
   */
  async getComplianceByCategory(warningDays: number = 30): Promise<{ categories: ComplianceByCategory[] }> {
    return httpClient.get(`${this.compliancePath}/by-category?warning_days=${warningDays}`);
  }

  /**
   * Set or update document expiration date.
   */
  async setDocumentExpiry(docId: string, expiresAt: string): Promise<{ success: boolean; message: string }> {
    return httpClient.post(`${this.compliancePath}/${docId}/set-expiry`, {
      expires_at: expiresAt,
    });
  }

  /**
   * Mark a document as renewed with new expiry date.
   */
  async markDocumentRenewed(
    docId: string,
    newExpiry: string,
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    return httpClient.post(`${this.compliancePath}/${docId}/mark-renewed`, {
      new_expiry: newExpiry,
      notes,
    });
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Copy portal URL to clipboard.
   */
  copyPortalUrl(url: string): Promise<boolean> {
    return navigator.clipboard
      .writeText(url)
      .then(() => true)
      .catch(() => false);
  }

  /**
   * Generate a share message for the upload link.
   */
  generateShareMessage(link: SecureUploadLink): string {
    const expiresDate = new Date(link.expires_at).toLocaleDateString();
    return `Please upload your documents using this secure link: ${link.portal_url}\n\nThis link expires on ${expiresDate}.${link.message_to_client ? `\n\nNote: ${link.message_to_client}` : ''}`;
  }
}

export const secureUploadLinkService = new SecureUploadLinkService();
